import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import type { MonthlyClientActivityData } from '../../domain/statisticsClients';
import ChartPlaceholder from './ChartPlaceholder';

interface MonthlyClientActivityChartProps {
    readonly data: readonly MonthlyClientActivityData[];
    readonly loading?: boolean;
}

function MonthlyClientActivityChart({
    data,
    loading = false,
}: MonthlyClientActivityChartProps): JSX.Element {
    const { t } = useTranslation();

    if (loading || data.length === 0) {
        return <ChartPlaceholder loading={loading} hasData={data.length > 0} />;
    }

    const options: Highcharts.Options = {
        chart: {
            type: 'column',
            height: 350,
        },
        title: {
            text: undefined,
        },
        xAxis: {
            categories: data.map(item => item.period),
            title: {
                text: t('statistics.clients.month', 'Month'),
            },
            labels: {
                rotation: -45,
            },
        },
        yAxis: {
            min: 0,
            allowDecimals: false,
            title: {
                text: t('statistics.clients.monthlyActivity', 'Monthly activity'),
            },
        },
        tooltip: {
            shared: true,
            valueDecimals: 0,
        },
        plotOptions: {
            column: {
                stacking: 'normal',
                borderWidth: 0,
            },
        },
        series: [
            {
                type: 'column',
                name: t('statistics.clients.activeClients', 'Clients with contracts'),
                data: data.map(item => item.activeClients),
                color: '#1890ff',
            },
            {
                type: 'column',
                name: t('statistics.clients.contractsCreated', 'Contracts created'),
                data: data.map(item => item.contracts),
                color: '#52c41a',
            },
        ],
        credits: {
            enabled: false,
        },
        legend: {
            enabled: true,
        },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export default MonthlyClientActivityChart;
